// deployment_class: customer_env_allowed
/**
 * The live executive payload, shaped for the content seam (UI-B2 · B3 · B7).
 *
 * ── What this replaces ─────────────────────────────────────────────────────
 * `dashboard-data.ts` states `$478M` across `10 active partnerships` in four
 * places. The database holds FOUR partnerships. The cockpit has been overstating
 * to the CEO by six because the surface is hand-authored constants rather than a
 * query.
 *
 * SCOPE, STATED HONESTLY. I expected this to close UI-B2, UI-B3 and UI-B7
 * together, since the board names one cause for all three. It does not. This
 * fixes the FIGURES (UI-B7's first half). UI-B2 is the rail ignoring a project's
 * chosen modules — a different surface that still never reads `MODULE_CATALOG`.
 * UI-B3 is the canned `SUGGESTED` array in `ask.ts`, untouched here. Both remain
 * open, and the board says so rather than inheriting an optimistic claim.
 *
 * `GET /v3/executive/dashboard` has existed and worked this whole time, and
 * `Dashboard.tsx:467` has always READ the seam — merging supplied content over
 * the bare module. What never existed was a WRITER: nothing ever called
 * `DashboardContentProvider`, so the reader always saw `null` and every figure
 * fell back to a constant. The reader was built, the writer was not. This is
 * the writer.
 *
 * ── The wire type is DECLARED here, not imported ───────────────────────────
 * `src/v3/executive/types.ts` is across the build boundary the frontend cannot
 * import through — the same constraint `CSRF_COOKIE_NAME` lives under. So the
 * shape is restated and PINNED by test against the backend's own field names.
 *
 * ── The rules that carry over, because they are the honest part ────────────
 * 1. `value: null` means "this agency has no answer yet" and MUST render as
 *    ABSENT. Not a zero, not a dash, not a placeholder. The backend's own type
 *    says so, and a stand-in reads as data. So a null metric is DROPPED here
 *    rather than formatted — the one rule most likely to be lost in a mapping.
 * 2. The two empty states are DIFFERENT truths. `no_records_yet` fills on the
 *    next import; `no_source_yet` fills only when somebody builds the entity.
 *    Collapsing them into "no data" would tell a CEO to go import something
 *    that would not help.
 *
 * ── What this deliberately does NOT do, and why ────────────────────────────
 * PROVENANCE IS DROPPED, AND THAT IS A GAP WORTH NAMING. Every `ExecutiveMetric`
 * carries a `source` so a figure can be followed back to the record behind it —
 * the property the Overview tab's own description promises ("each figure carries
 * where it came from"). The seam's `Kpi` is `{k, v, s}` and ALL THREE DISPLAY,
 * and `source.ref` is a table name. Putting it in `s` would render schema
 * vocabulary at a CEO, which E3 explicitly ruled out. So provenance is carried
 * no further than here until `Kpi` has somewhere non-visible to put it.
 * Logged rather than silently discarded.
 */
import type { ModuleKey } from './dashboard-data';
import type { DashboardContent } from './dashboard-source';

/** Mirrors `src/v3/executive/types.ts` · pinned by test. */
export interface WireMetricSource {
  readonly kind: 'table' | 'engine';
  readonly ref: string;
}

/** Mirrors `ExecutiveMetric`. `value: null` = no answer yet — render as absent. */
export interface WireMetric {
  readonly key: string;
  readonly label: string;
  readonly value: number | string | null;
  readonly source: WireMetricSource;
}

/** Mirrors `ModuleAvailability` — the two empty states are distinct truths. */
export type WireAvailability =
  | { readonly state: 'populated' }
  | { readonly state: 'empty'; readonly reason: 'no_records_yet' }
  | { readonly state: 'empty'; readonly reason: 'no_source_yet'; readonly awaiting: string };

/** Mirrors `ExecutiveModuleView`. */
export interface WireModuleView {
  readonly key: 'board' | 'planning' | 'projects' | 'oversight';
  readonly label: string;
  readonly availability: WireAvailability;
  readonly metrics: readonly WireMetric[];
}

/** Mirrors `ExecutiveDashboard`. */
export interface WireExecutiveDashboard {
  readonly organization_id: string;
  readonly generated_at: string;
  readonly rollup: readonly WireMetric[];
  readonly modules: readonly WireModuleView[];
}

/**
 * Format a metric for display, or `null` when it must not be displayed at all.
 *
 * The null case is the whole point: an agency with no answer yet gets NOTHING,
 * because a `0` and a `—` both read as findings.
 */
function formatValue(value: WireMetric['value']): string | null {
  if (value === null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString() : null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Metrics that can honestly be shown, as the seam's KPI shape.
 *
 * `s` is left EMPTY rather than filled with `source.ref`: every field of `Kpi`
 * renders, and `ref` is a table name. See the module docblock.
 */
function toKpis(metrics: readonly WireMetric[]): { k: string; v: string; s: string }[] {
  const out: { k: string; v: string; s: string }[] = [];
  for (const m of metrics) {
    const v = formatValue(m.value);
    if (v === null) continue; // absent, not zero — rule 1
    out.push({ k: m.label, v, s: '' });
  }
  return out;
}

/**
 * True when the payload has nothing an agency could read as a figure.
 *
 * Distinct from "no supplier": this says a supplier answered and had nothing,
 * which is what `DashboardContent.empty` exists to express.
 */
function isEmptyPayload(dash: WireExecutiveDashboard): boolean {
  if (toKpis(dash.rollup).length > 0) return false;
  // A module the backend calls `empty` contributes nothing regardless of what
  // its metrics format to — the same rule `toDashboardContent` applies. Without
  // this, an agency that had imported nothing produced three "0" strings here
  // and the payload reported itself as NOT empty, which is the opposite of the
  // truth and the reason `empty` exists.
  return dash.modules.every(
    (m) => m.availability.state === 'empty' || toKpis(m.metrics).length === 0,
  );
}

/**
 * Map the live payload onto the content seam.
 *
 * `rollup` becomes the Executive module's own figures — the backend models
 * Executive as the confluence rather than a feeder, which is why it is absent
 * from `ExecutiveModuleKey` and arrives separately.
 *
 * ── `availability` is HONOURED, not just carried (2026-09-03) ──────────────
 * E3: *"our template is just a container … So far, there's no data, so we
 * should not be showing any sort of values."*
 *
 * A module the backend calls `empty` contributes NO kpis. Previously its
 * metrics were mapped like any other module's, and a count of zero formats to
 * `"0"` — so an agency that had imported nothing was shown `0 / 0 / 0`. Three
 * zeroes are three FIGURES. A CEO reads them as findings: "we have zero
 * development projects" is an assertion about the portfolio, not an admission
 * that nothing has been uploaded.
 *
 * The docblock's rule 1 already said a null must render absent "not a zero" —
 * but the rule was enforced only on `null`, and the backend sends a real `0`
 * for an empty count, which is the honest thing for it to send. The distinction
 * the backend draws in `availability` is where "we have none" and "nothing has
 * arrived" separate, and it was being discarded here.
 *
 * This is the same shape as the emptying work: the template is SHAPE, values
 * are DATA, and shape must render without data
 * ([[project_composition_vocabulary_lock_2026_08_27]]).
 */
export function toDashboardContent(dash: WireExecutiveDashboard): DashboardContent {
  const modules: Partial<Record<ModuleKey, { kpis: { k: string; v: string; s: string }[] }>> = {};

  const rollup = toKpis(dash.rollup);
  if (rollup.length > 0) modules.executive = { kpis: rollup };

  for (const m of dash.modules) {
    // An empty module contributes nothing. Not zeroes, not placeholders —
    // the container renders on its own and says nothing it has not earned.
    if (m.availability.state === 'empty') continue;
    const kpis = toKpis(m.metrics);
    if (kpis.length > 0) modules[m.key] = { kpis };
  }

  return { modules, empty: isEmptyPayload(dash) };
}
